import { safeReturnTo as sharedSafeReturnTo } from '@misumi/shared'

/**
 * Same-origin-only "return to this path after auth" helper.
 *
 * Delegates to the ONE canonical validator in `@misumi/shared`
 * (`safeReturnTo`, see packages/shared/src/safe-return-to.ts) so the SPA and
 * the BFF share a single definition of a safe destination — belt-and-
 * suspenders, since the BFF re-validates independently (never trust the
 * client). This wrapper keeps the SPA-local `(candidate, fallback) => string`
 * ergonomics: the shared guard returns `null` for anything unsafe, and we
 * substitute the caller's fallback. USP-41: the shared guard rejects ASCII
 * control chars (tab/CR/LF), which browsers strip before parsing and which a
 * naive leading-character check let through.
 */
export function safeReturnTo(candidate: unknown, fallback: string): string {
  return sharedSafeReturnTo(candidate) ?? fallback
}

/**
 * Builds the URL for the BFF's OIDC login entry point (`GET /api/auth/login`
 * — a redirect/operational flow, intentionally outside the JSON contract, see
 * apps/api/openapi.yaml's `auth` tag description). This is a **full-page
 * navigation target**, never a `fetch()` call — the BFF replies with a 302 to
 * the IdP, and the whole point of PKCE + the BFF-mediated session (C22) is
 * that the browser never touches the resulting tokens.
 *
 * `returnTo` is only forwarded when it survives {@link safeReturnTo} against
 * `/` — an unsafe or no-op candidate is simply omitted rather than forwarded
 * as-is, so a bad value can't round-trip through the query string at all.
 */
export function buildLoginUrl(apiBaseUrl: string, returnTo?: string): string {
  const loginPath = `${apiBaseUrl}/auth/login`
  if (!returnTo) {
    return loginPath
  }
  const safe = safeReturnTo(returnTo, '/')
  if (safe === '/') {
    return loginPath
  }
  return `${loginPath}?returnTo=${encodeURIComponent(safe)}`
}
